import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { openCalendlyPopup } from "../lib/calendly";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/blog", label: "Journal" },
  { to: "/book", label: "Book" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="cf-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#F4F2F0]/75 border-b border-[#D1CDC7]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="cf-logo" className="flex items-center gap-2 group">
          <span className="w-8 h-8 border border-[#1A1C1A] flex items-center justify-center">
            <span className="font-serif italic text-lg leading-none">C</span>
          </span>
          <span className="font-serif text-xl tracking-tight">
            CareerFactors<span className="text-[#C26D5C]">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`} className="nav-link">
              {({ isActive }) => (
                <span data-active={isActive ? "true" : "false"} className="nav-link">{l.label}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <button type="button" onClick={openCalendlyPopup} data-testid="nav-cta" className="btn-cf">
            Book a Free Call
            <span aria-hidden>→</span>
          </button>
        </div>

        <button
          data-testid="cf-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-10 h-10 border border-[#1A1C1A] flex items-center justify-center"
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.4, ease: [0.33, 1, 0.68, 1] }}
            className="md:hidden overflow-hidden bg-[#F4F2F0] border-t border-[#D1CDC7]"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="font-serif text-3xl tracking-tight"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/book" data-testid="mobile-nav-cta" className="btn-cf mt-4 w-fit">
                Book a Free Call <span aria-hidden>→</span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
